/**
 * CloudAPIPanel — v2.0.0 Phase 3
 *
 * Settings → WhatsApp section that lets the admin configure the Meta
 * Cloud API for the currently-active project. When enabled and properly
 * filled, the backend automatically routes :
 *   - welcome auto-sends through Cloud (if a template is registered)
 *   - bulk-send campaigns through Cloud (if a template is picked in dialog)
 *   - any free-form send outside the 24h window through Cloud
 * while keeping Baileys for in-window conversation.
 *
 * The panel is intentionally minimal — credentials, welcome template name,
 * test-connexion button. The full template-management UI (submit / edit /
 * review status) is delegated to the Meta Business Manager because their
 * tooling is better than anything we'd build here.
 *
 * Admin-only — the parent guards visibility. Backend also enforces 403
 * via update_project / cloud_health.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Globe,
  Save,
  Loader2,
  Check,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { projectsApi, whatsappApi } from "@/lib/crmApi";

export default function CloudAPIPanel({ projet, onSaved }) {
  // Local form state. Pre-fill with whatever's on the project doc, fall
  // back to empty strings so no controlled-input warning.
  const [form, setForm] = useState(() => emptyForm(projet));
  const [saving, setSaving] = useState(false);
  // Health probe state — runs against Meta on click. Stays after success
  // so the admin sees "OK — SOGI Immobilier" until they navigate away.
  const [healthState, setHealthState] = useState(null);
  const [healthing, setHealthing] = useState(false);
  // Toggle to reveal/hide the access token (looks like a password by default)
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  // Reset form whenever the user switches projects from the topbar.
  useEffect(() => {
    setForm(emptyForm(projet));
    setHealthState(null);
  }, [projet?.id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await projectsApi.update(projet.id, {
        cloud_enabled: form.cloud_enabled,
        cloud_phone_id: form.cloud_phone_id.trim(),
        cloud_business_account_id: form.cloud_business_account_id.trim(),
        cloud_access_token: form.cloud_access_token.trim(),
        cloud_webhook_secret: form.cloud_webhook_secret.trim(),
        cloud_welcome_template: form.cloud_welcome_template.trim(),
        cloud_welcome_template_lang: form.cloud_welcome_template_lang.trim() || "fr",
      });
      toast.success("Configuration Cloud API enregistrée");
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error(detail || "Seul un administrateur peut configurer le Cloud API.");
      } else {
        toast.error(detail || e?.message || "Échec de l'enregistrement");
      }
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setHealthing(true);
    setHealthState(null);
    try {
      const r = await whatsappApi.cloud.health(projet.id);
      setHealthState({ ok: r.ok, info: r.info });
      if (r.ok) {
        toast.success(`Cloud API OK · ${r.info}`);
      } else {
        toast.error(`Connexion refusée · ${r.info}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setHealthState({ ok: false, info: detail || e?.message || "erreur" });
      toast.error(`Échec test · ${detail || e?.message || "erreur"}`);
    } finally {
      setHealthing(false);
    }
  };

  // Webhook URL the admin must declare in their Meta Business Manager.
  // Reads window.location to suggest the right host — works in dev (localhost)
  // and prod (crm.sogi.sn) without a config.
  const webhookUrl = `${window.location.protocol}//${window.location.host}/api/whatsapp/cloud/webhook`;
  const copyUrl = () => {
    try {
      navigator.clipboard.writeText(webhookUrl);
      toast.success("URL copiée");
    } catch {
      toast.error("Impossible de copier — sélectionne manuellement");
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-blue-200/60 bg-gradient-to-br from-blue-50/40 to-card p-5 shadow-soft dark:border-blue-900/30 dark:from-blue-950/20 dark:to-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
            <Globe className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold">
              Cloud API officielle (Meta)
              <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                v2.0
              </span>
            </h3>
            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Quand activée, les <b>envois hors fenêtre 24h</b> (welcome auto + campagnes
              groupées) passent par l'API officielle Meta plutôt que par Baileys —{" "}
              <b>aucun risque de suspension</b>. Les réponses live restent gratuites via
              Baileys dans la fenêtre 24h.
            </p>
          </div>
        </div>
        <Switch
          checked={form.cloud_enabled}
          onCheckedChange={(v) => update("cloud_enabled", v)}
          data-testid="cloud-enabled-switch"
        />
      </div>

      {/* Webhook URL helper — visible TOUJOURS, c'est ce que l'admin doit
          copier dans le Meta Business Manager. */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs dark:border-amber-900/40 dark:bg-amber-950/40">
        <p className="font-semibold text-amber-900 dark:text-amber-200">
          URL Webhook à déclarer dans Meta
        </p>
        <p className="mt-1 text-amber-800 dark:text-amber-300">
          Dans <b>Meta Business Manager → WhatsApp → Configuration → Webhooks</b>,
          colle l'URL ci-dessous et utilise <b>le même secret</b> que le champ « Secret
          du webhook » plus bas comme verify token.
        </p>
        <div className="mt-2 flex items-center gap-2 rounded bg-white/60 px-2 py-1.5 font-mono text-[11px] text-amber-900 dark:bg-black/20 dark:text-amber-200">
          <span className="truncate">{webhookUrl}</span>
          <button
            type="button"
            onClick={copyUrl}
            className="ml-auto flex-none rounded bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide hover:bg-amber-300 dark:bg-amber-900 dark:hover:bg-amber-800"
          >
            Copier
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <FormField
          label="Phone Number ID"
          help='Visible dans Meta : WhatsApp → API Setup → "phone_number_id"'
          value={form.cloud_phone_id}
          onChange={(v) => update("cloud_phone_id", v)}
          placeholder="123456789012345"
          testId="cloud-phone-id"
        />
        <FormField
          label="WhatsApp Business Account ID"
          help='Visible dans Meta : Business Settings → Comptes → WhatsApp → ID'
          value={form.cloud_business_account_id}
          onChange={(v) => update("cloud_business_account_id", v)}
          placeholder="987654321098765"
          testId="cloud-business-id"
        />

        <FormField
          label="Access Token (System User permanent)"
          help="Token d'utilisateur système Meta (Business Settings → System Users). NE PAS utiliser le token de 24h."
          value={form.cloud_access_token}
          onChange={(v) => update("cloud_access_token", v)}
          placeholder="EAAxxxxx..."
          type={showToken ? "text" : "password"}
          rightAddon={
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              className="text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          }
          testId="cloud-access-token"
          className="md:col-span-2"
        />

        <FormField
          label="Secret du Webhook (verify token)"
          help="Chaîne aléatoire que TU choisis. Le même mot doit être saisi côté Meta dans la configuration du webhook."
          value={form.cloud_webhook_secret}
          onChange={(v) => update("cloud_webhook_secret", v)}
          placeholder="ex : sogi_wh_2026_aBcDeFg123"
          type={showSecret ? "text" : "password"}
          rightAddon={
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="text-muted-foreground hover:text-foreground"
              tabIndex={-1}
            >
              {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          }
          testId="cloud-webhook-secret"
          className="md:col-span-2"
        />
      </div>

      <div className="mt-5 border-t border-border/60 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Template de welcome
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Quand un nouveau lead est créé, le welcome partira via Cloud avec ce template
          au lieu de Baileys (free-form). Le template doit être <b>APPROVED</b> côté Meta
          et accepter <code className="rounded bg-muted px-1">{"{{1}}"}</code> = prénom.
        </p>
        <div className="mt-3 grid gap-3 md:grid-cols-[1fr_120px]">
          <FormField
            label="Nom du template"
            value={form.cloud_welcome_template}
            onChange={(v) => update("cloud_welcome_template", v)}
            placeholder="ex : sogi_welcome_v1"
            testId="cloud-welcome-template"
          />
          <FormField
            label="Langue"
            value={form.cloud_welcome_template_lang}
            onChange={(v) => update("cloud_welcome_template_lang", v)}
            placeholder="fr"
            testId="cloud-welcome-template-lang"
          />
        </div>
      </div>

      {/* Action bar : Test + Save. Test seulement actif si la conf minimale
          est saisie ET la switch principale est ON. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-4">
        <div className="text-xs">
          {healthState && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 font-semibold ${
                healthState.ok
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  : "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
              }`}
            >
              {healthState.ok ? (
                <Check className="h-3 w-3" />
              ) : (
                <AlertCircle className="h-3 w-3" />
              )}
              {healthState.info}
            </span>
          )}
          {!healthState && form.cloud_enabled && (
            <span className="text-muted-foreground">
              Activée — sauvegarde puis teste la connexion.
            </span>
          )}
          {!form.cloud_enabled && (
            <span className="text-muted-foreground">
              Désactivée — les envois continuent via Baileys.
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={testConnection}
            disabled={
              healthing ||
              !form.cloud_enabled ||
              !form.cloud_phone_id.trim() ||
              !form.cloud_access_token.trim()
            }
            className="gap-1.5"
            data-testid="cloud-test-btn"
          >
            {healthing ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Test…
              </>
            ) : (
              <>
                <Plug className="h-3.5 w-3.5" />
                Tester la connexion
              </>
            )}
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="gap-1.5 bg-blue-600 text-white hover:bg-blue-700"
            data-testid="cloud-save-btn"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* Doc shortcut */}
      <p className="mt-3 text-[11px] text-muted-foreground">
        Pas encore de compte Meta Business pour SOGI ? Suivez le guide{" "}
        <a
          href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 text-blue-600 hover:underline dark:text-blue-400"
        >
          Get Started Cloud API
          <ExternalLink className="h-2.5 w-2.5" />
        </a>{" "}
        (numéro de test gratuit dispo immédiatement, 250 conv/mois).
      </p>
    </div>
  );
}

/** Reusable form field with help text + optional right addon (eye toggle). */
function FormField({
  label,
  help,
  value,
  onChange,
  placeholder,
  type = "text",
  rightAddon,
  testId,
  className = "",
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          data-testid={testId}
          className={rightAddon ? "pr-9" : ""}
        />
        {rightAddon && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">{rightAddon}</div>
        )}
      </div>
      {help && (
        <p className="text-[10px] leading-relaxed text-muted-foreground">{help}</p>
      )}
    </div>
  );
}

function emptyForm(projet) {
  return {
    cloud_enabled: !!projet?.cloud_enabled,
    cloud_phone_id: projet?.cloud_phone_id || "",
    cloud_business_account_id: projet?.cloud_business_account_id || "",
    cloud_access_token: projet?.cloud_access_token || "",
    cloud_webhook_secret: projet?.cloud_webhook_secret || "",
    cloud_welcome_template: projet?.cloud_welcome_template || "",
    cloud_welcome_template_lang: projet?.cloud_welcome_template_lang || "fr",
  };
}
